/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { FieldValues } from 'react-hook-form';
import {
  useForm,
  getFormProps,
  getFieldProps,
  getSubmitButtonProps,
  useApiClient,
} from '@marageti/z4-lib';
import { Stack, TextField } from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { LoadingButton } from '@mui/lab';
import { useSearchParams } from 'react-router-dom';

const schema = z.object({
  email: z.string().email('Please enter a valid email address').nonempty('Required'),
});

interface EmailLinkFormProps {
  email?: string;
  onSuccess: (email: string) => void;
  type: 'activate' | 'reset';
}

const EmailLinkForm = ({
  email = undefined,
  onSuccess,
  type,
}: EmailLinkFormProps) => {
  const apiClient = useApiClient();
  const [search] = useSearchParams();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    defaultValues: { email },
    resolver: zodResolver(schema),
  });

  const onSubmit = (data: FieldValues): Promise<unknown> => {
    // All cases handled as successful submits.
    // Only emails with accounts will receive an email.
    // Maintain all search parameters for the email link.
    const queryParams: Record<string, string> = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of search.entries()) {
      queryParams[key] = value;
    }
    return apiClient.authClient.postUserReset(data.email, { params: queryParams })
      .catch(console.warn)
      .finally(() => {
        onSuccess(data.email);
      });
  };

  const getButtonText = () => {
    if (type === 'reset') {
      return email ? 'Resend Link' : 'Reset Password';
    }
    return 'Send New Link';
  };

  return (
    <form
      name="send-link-form"
      {...getFormProps(handleSubmit(onSubmit))}
    >
      <Stack spacing={3} width={340}>
        {!email && (
          <Stack>
            <TextField
              id="email"
              InputProps={{
                readOnly: type === 'activate',
              }}
              label="Email"
              {...register('email')}
              {...getFieldProps('email', errors)}
            />
          </Stack>
        )}

        <LoadingButton
          type="submit"
          fullWidth
          variant="contained"
          {...getSubmitButtonProps(isSubmitting, isValid)}
        >
          {getButtonText()}
        </LoadingButton>
      </Stack>
    </form>
  );
};

export default EmailLinkForm;
